import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Copy, KeyRound, Loader2, Trash2 } from "lucide-react";

interface HostApp { id: string; last4: string; fullAccess: boolean; createdAt: string; source: "settings" | "environment" }
interface Overview { clients: HostApp[]; secretFrom: "environment" | "settings" | "none"; enabled: boolean }

/** Master settings > Integrations: service keys for host products (SalesPro, LeadPro) that embed or drive VoicePro. */
export default function HostAppsSettings() {
  const { toast } = useToast();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [appId, setAppId] = useState("salespro");
  const [fullAccess, setFullAccess] = useState(true);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ id: string; key: string } | null>(null);

  const load = useCallback(async () => {
    try { setOverview(await (await apiRequest("GET", "/api/admin/embed-clients")).json()); }
    catch (e) { toast({ title: "Could not load host apps", description: (e as Error).message, variant: "destructive" }); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    const existing = overview?.clients.find((c) => c.id === appId.trim().toLowerCase());
    if (existing && !window.confirm(`"${existing.id}" already has a key. A new key replaces it, and the app stops working until you paste the new one. Continue?`)) return;
    setBusy(true);
    try {
      const r = await apiRequest("POST", "/api/admin/embed-clients", { id: appId, fullAccess });
      const data = await r.json();
      setCreated({ id: data.id, key: data.key });
      await load();
    } catch (e) { toast({ title: "Could not generate the key", description: (e as Error).message, variant: "destructive" }); }
    setBusy(false);
  };

  const revoke = async (app: HostApp) => {
    if (!window.confirm(`Revoke the key for "${app.id}"? That app will stop being able to use VoicePro.`)) return;
    try { await apiRequest("DELETE", `/api/admin/embed-clients/${encodeURIComponent(app.id)}`); setCreated(null); await load(); }
    catch (e) { toast({ title: "Could not revoke", description: (e as Error).message, variant: "destructive" }); }
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast({ title: "Copied" }); } catch { toast({ title: "Copy failed", description: "Select the key and copy it by hand.", variant: "destructive" }); }
  };

  return (
    <Card id="host-apps">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Host apps (SalesPro, LeadPro)</CardTitle>
        <CardDescription>
          Generate the service key a host app pastes into its own settings (SalesPro: Platform Admin &gt; AI &amp; Email &gt; AI Voice). Full access lets the app
          manage agents, flows, campaigns, calls and analytics here, not only the setup wizard.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {overview && !overview.enabled && <Alert><AlertDescription>Embedding is off until the first key is generated.</AlertDescription></Alert>}
        {overview && overview.secretFrom === "settings" && <p className="text-xs text-muted-foreground">Tokens are signed with a secret generated here. Set EMBED_JWT_SECRET on the server to use your own instead.</p>}

        {overview && overview.clients.length > 0 && (
          <ul className="divide-y rounded-md border">
            {overview.clients.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                <div>
                  <p className="font-medium">{c.id} <span className="font-mono text-xs text-muted-foreground">…{c.last4}</span></p>
                  <p className="text-xs text-muted-foreground">
                    {c.fullAccess ? "Full access" : "Setup wizard only"} · {c.source === "environment" ? "set in the server environment (change it there)" : `generated ${new Date(c.createdAt).toLocaleDateString()}`}
                  </p>
                </div>
                {c.source === "settings" && <Button variant="ghost" size="sm" onClick={() => revoke(c)}><Trash2 className="h-4 w-4 mr-1" />Revoke</Button>}
              </li>
            ))}
          </ul>
        )}

        <div className="grid gap-3 md:grid-cols-[200px_1fr_auto] md:items-end">
          <div className="space-y-1"><Label htmlFor="host-app-id">App id</Label><Input id="host-app-id" value={appId} onChange={(e) => setAppId(e.target.value.toLowerCase())} placeholder="salespro" /></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={fullAccess} onCheckedChange={(v) => setFullAccess(v === true)} /> Full access (agents, flows, campaigns, calls, analytics)</label>
          <Button onClick={generate} disabled={busy || !appId.trim()}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate key"}</Button>
        </div>

        {created && (
          <Alert>
            <AlertDescription className="space-y-2">
              <p className="font-medium">Key for {created.id}. Copy it now: it is shown once and cannot be read again.</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-muted p-2 text-xs">{created.key}</code>
                <Button variant="outline" size="sm" onClick={() => copy(created.key)}><Copy className="h-4 w-4 mr-1" />Copy</Button>
              </div>
              <p className="text-xs text-muted-foreground">Paste it into the host app. Anything already using the old key for this app stops working.</p>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
